import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFirestore, useFirestoreDocData, useFunctions } from 'reactfire';
import { doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  Box,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Container,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Divider,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import useGlobal from 'global-state/store';
import { downloadPdfFromStorage } from 'utils/storageUtils';
import CertificateMetadataForm from './CertificateMetadataForm';
import PerPageTableParser, { VALIDATION_RULES_DESCRIPTION } from './PerPageTableParser';
import UnifiedDataView, { createUnifiedData } from './UnifiedDataView';
import ScrollablePdfViewer from './ScrollablePdfViewer';
import ResizableSplitView from './ResizableSplitView';

const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

const getReprocessErrorMessage = (reprocessError) => {
  const backendMessage = reprocessError?.message || '';

  if (backendMessage.includes('Stored raw Document AI JSON is unavailable')) {
    return 'Les donnees Document AI brutes ne sont pas disponibles pour ce document. '
      + 'Relancez Document AI pour le retraiter.';
  }

  return backendMessage || 'Erreur lors du retraitement du document';
};

const mergeTablesByPage = (tables = []) => {
  if (!Array.isArray(tables)) {
    return [];
  }

  const tablesByPage = tables.reduce((acc, table, index) => {
    const tableData = table || {};
    const pageKey = typeof tableData.pageIndex === 'number' ? tableData.pageIndex : index;
    if (!acc[pageKey]) {
      acc[pageKey] = [];
    }
    acc[pageKey].push(tableData);
    return acc;
  }, {});

  return Object.entries(tablesByPage).map(([pageKey, pageTables]) => {
    const pageNumber = Number(pageKey);
    const dataPoints = [];
    let confidence = 0;

    pageTables.forEach((table) => {
      if (typeof table?.confidence === 'number') {
        confidence = Math.max(confidence, table.confidence);
      }

      table?.compartments?.forEach((compartment) => {
        const {
          heights = [],
          volumes = [],
          columnIndices = {},
        } = compartment || {};

        if (!Array.isArray(heights) || !Array.isArray(volumes)) {
          return;
        }

        if (heights.length === 0 || heights.length !== volumes.length) {
          return;
        }

        const heightCol = columnIndices.height;
        const volumeCol = columnIndices.volume;

        if (
          typeof heightCol === 'number'
          && typeof volumeCol === 'number'
          && volumeCol <= heightCol
        ) {
          return;
        }

        heights.forEach((height, idx) => {
          const volume = volumes[idx];
          if (isFiniteNumber(height) && isFiniteNumber(volume)) {
            dataPoints.push({ height, volume });
          }
        });
      });
    });

    if (dataPoints.length === 0) {
      const fallback = pageTables[0]?.compartments?.[0];

      if (
        Array.isArray(fallback?.heights)
        && Array.isArray(fallback?.volumes)
        && fallback.heights.length === fallback.volumes.length
      ) {
        fallback.heights.forEach((height, idx) => {
          const volume = fallback.volumes[idx];
          if (isFiniteNumber(height) && isFiniteNumber(volume)) {
            dataPoints.push({ height, volume });
          }
        });
      }
    }

    const uniqueSortedPoints = [];
    const seen = new Set();

    dataPoints
      .sort((a, b) => (a.height === b.height ? a.volume - b.volume : a.height - b.height))
      .forEach((point) => {
        const key = point.height.toFixed(5);
        if (!seen.has(key)) {
          uniqueSortedPoints.push(point);
          seen.add(key);
        }
      });

    return {
      id: `page_${pageNumber}`,
      pageNumber,
      tableIndex: 0,
      compartmentId: `page_${pageNumber}`,
      heights: uniqueSortedPoints.map((p) => p.height),
      volumes: uniqueSortedPoints.map((p) => p.volume),
      dataPointIds: uniqueSortedPoints.map((_, index) => `dp_${pageNumber}_0_${index}_${Date.now()}`),
      confidence: confidence || pageTables[0]?.confidence || null,
    };
  }).sort((a, b) => a.pageNumber - b.pageNumber);
};

export default function ScannedCertificateViewer() {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const firestore = useFirestore();
  const functions = useFunctions();
  functions.region = 'europe-west1';
  const [globalState, globalActions] = useGlobal();

  const scanRef = globalState.activeOrganization
    ? doc(
      firestore,
      `organizations/${globalState.activeOrganization}/apps/digitank-gauger/scannedCertificates/${scanId}`,
    )
    : null;

  const { data: scanData, status } = useFirestoreDocData(scanRef);

  // State management for the split view approach
  const [extractedData, setExtractedData] = useState(null);
  const [metadata, setMetadata] = useState({});
  const [pageTables, setPageTables] = useState([]);
  const [processing] = useState(false);
  const [error, setError] = useState(null);
  const [highlightedPages, setHighlightedPages] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [reprocessing, setReprocessing] = useState(false);
  const [showReprocessDialog, setShowReprocessDialog] = useState(false);
  const [selectedPdfPage, setSelectedPdfPage] = useState(1);
  const [reprocessEngine, setReprocessEngine] = useState('parser');
  const [reprocessScope, setReprocessScope] = useState('document');
  const [reprocessPageNumber, setReprocessPageNumber] = useState(1);
  const [hasLocalEdits, setHasLocalEdits] = useState(false);
  const [showDiscardLocalEditsDialog, setShowDiscardLocalEditsDialog] = useState(false);

  const parserInfo = scanData?.parser || {};
  const confidenceScore = (() => {
    if (typeof parserInfo.confidence === 'number') {
      return parserInfo.confidence;
    }
    if (typeof extractedData?.confidence === 'number') {
      return extractedData.confidence;
    }
    return null;
  })();
  const confidenceThreshold = typeof parserInfo.confidenceThreshold === 'number'
    ? parserInfo.confidenceThreshold
    : 0.7;
  const lowConfidence = parserInfo.lowConfidence ?? (typeof confidenceScore === 'number'
    ? confidenceScore < confidenceThreshold
    : false);
  const confidencePercent = typeof confidenceScore === 'number'
    ? Math.round(confidenceScore * 100)
    : null;
  const parserWarnings = Array.isArray(parserInfo.warnings) ? parserInfo.warnings : [];

  const confidenceChipColor = (() => {
    if (confidenceScore === null) return 'default';
    if (lowConfidence) return 'error';
    if (confidenceScore >= 0.85) return 'success';
    return 'warning';
  })();

  // Load scan data when available
  useEffect(() => {
    if (scanData && scanData.status === 'ready') {
      const { parsedResult } = scanData;
      if (parsedResult) {
        setExtractedData(parsedResult);

        // Initialize metadata from parsed data
        if (parsedResult.metadata) {
          setMetadata(parsedResult.metadata);
        }

        // Initialize page tables from parsed data
        if (parsedResult.tables) {
          const tables = mergeTablesByPage(parsedResult.tables);
          setPageTables(tables);
          setHasLocalEdits(false);

          const pages = [...new Set(tables
            .filter((table) => table.heights.length > 0)
            .map((table) => table.pageNumber + 1))];
          setHighlightedPages(pages);
        }
      }

      // Download PDF from Storage
      if (scanData.storage?.pdfPath) {
        downloadPdfFromStorage(scanData.storage.pdfPath)
          .then((url) => {
            setPdfUrl(url);
          })
          .catch((err) => {
            setError('Impossible de télécharger le PDF');
          });
      }
    } else if (scanData && scanData.status === 'error') {
      setError(scanData.error || 'Erreur lors du traitement du document');
    }
  }, [scanData]);

  const handleReprocessClick = () => {
    setReprocessPageNumber(selectedPdfPage || 1);
    setShowReprocessDialog(true);
  };

  const handleReprocessConfirm = async () => {
    setShowReprocessDialog(false);

    if (!scanId || !globalState.activeOrganization) return;

    try {
      setReprocessing(true);
      setError(null);

      const reprocessCertificatePdf = httpsCallable(
        functions,
        'reprocessCertificatePdfCall2ndGen',
      );

      const result = await reprocessCertificatePdf({
        organizationId: globalState.activeOrganization,
        scanId,
        reprocessEngine,
        reprocessScope,
        pageNumber: reprocessScope === 'page' ? Number(reprocessPageNumber) : undefined,
      });

      if (result.data.success) {
        // Update local state with reprocessed data
        const newData = result.data.data;
        setExtractedData(newData);

        if (newData.metadata) {
          setMetadata(newData.metadata);
        }

        if (newData.tables) {
          const tables = mergeTablesByPage(newData.tables);
          setPageTables(tables);
          setHasLocalEdits(false);

          const pages = [...new Set(tables
            .filter((table) => table.heights.length > 0)
            .map((table) => table.pageNumber + 1))];
          setHighlightedPages(pages);
        }
        globalActions.setSnackbarMessage({
          message: 'Retraitement terminé avec succès.',
          severity: 'success',
        });
      } else {
        const errorMessage = result.data.error || 'Échec du retraitement';
        setError(errorMessage);
        globalActions.setSnackbarMessage({
          message: errorMessage,
          severity: 'error',
        });
      }
    } catch (reprocessError) {
      const errorMessage = getReprocessErrorMessage(reprocessError);
      setError(errorMessage);
      globalActions.setSnackbarMessage({
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setReprocessing(false);
    }
  };

  const handleReprocessCancel = () => {
    setShowReprocessDialog(false);
  };

  const handleMetadataChange = (newMetadata) => {
    setMetadata(newMetadata);
    setHasLocalEdits(true);
  };

  const handlePageTablesChange = (newPageTables) => {
    setPageTables(newPageTables);
    setHasLocalEdits(true);
  };

  const handleJumpToPage = (pageNumber) => {
    setSelectedPdfPage(pageNumber);
    if (window.pdfViewerRef && window.pdfViewerRef.scrollToPage) {
      window.pdfViewerRef.scrollToPage(pageNumber);
    }
  };

  const handlePdfPageClick = (pageNumber) => {
    setSelectedPdfPage(pageNumber);
    setReprocessPageNumber(pageNumber);
  };

  const handleBackToList = () => {
    if (hasLocalEdits) {
      setShowDiscardLocalEditsDialog(true);
      return;
    }
    navigate('/certificates/scanned');
  };

  const handleDiscardLocalEditsAndLeave = () => {
    setShowDiscardLocalEditsDialog(false);
    navigate('/certificates/scanned');
  };

  useEffect(() => {
    if (!hasLocalEdits) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasLocalEdits]);

  const handleExportCsv = (csvData) => {
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `certificate_${metadata.certificateId || 'data'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleExportInfoCsv = () => {
    const originalFileName = scanData?.originalFileName || 'document';
    const baseFileName = originalFileName.replace(/\.[^/.]+$/, ''); // Remove extension

    const csvRows = [];
    const header = 'proprietaire;id_recipient;id_chai;id_certificat;prestataire_jaugeage_pdf;'
      + 'date_de_la_verification;date_limite_de_validite;unites_de_hauteur;unites_de_volume;'
      + 'capacite_citerne;volume_nominal';
    csvRows.push(header);

    const infoRow = [
      metadata.owner || '',
      metadata.containerId || '',
      metadata.cellarId || '',
      metadata.certificateName || '',
      metadata.gaugerName || '',
      metadata.gaugingDate || '',
      metadata.validityDate || '',
      metadata.heightUnit || 'm',
      metadata.volumeUnit || 'm³',
      metadata.containerCapacity || '',
      metadata.nominalVolume || '',
    ].join(';');

    csvRows.push(infoRow);

    const csvData = csvRows.join('\n');
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseFileName}_info.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleExportDonneesCsv = () => {
    const originalFileName = scanData?.originalFileName || 'document';
    const baseFileName = originalFileName.replace(/\.[^/.]+$/, ''); // Remove extension

    const csvRows = [];
    csvRows.push('hauteur;volume');

    // Use unified data in the same order as displayed (no sorting/dedup)
    const unified = createUnifiedData(pageTables);
    unified.heights.forEach((height, i) => {
      const volume = unified.volumes[i];
      csvRows.push(`${Number(height).toFixed(3)};${Number(volume).toFixed(3)}`);
    });

    const csvData = csvRows.join('\n');
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseFileName}_donnees.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleExportBothCsv = () => {
    // Export both CSV files
    handleExportInfoCsv();
    // Add a small delay to ensure the first download starts before the second
    setTimeout(() => {
      handleExportDonneesCsv();
    }, 100);
  };

  // Loading state
  if (status === 'loading') {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Error state
  if (status === 'error' || !scanData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Document non trouvé
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            Le document scanné demandé n&apos;existe pas ou n&apos;est plus accessible.
          </Alert>
          <Button variant="contained" onClick={handleBackToList}>
            Retour à la liste
          </Button>
        </Paper>
      </Container>
    );
  }

  // Processing state
  if (scanData.status === 'processing') {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Traitement en cours...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Le document est en cours de traitement. Veuillez patienter.
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Main viewing interface
  return (
    <Box sx={{
      position: 'fixed',
      top: 'var(--app-header-height, 44px)',
      bottom: 'var(--app-footer-height, 0px)',
      left: 0,
      right: 0,
      width: '100vw',
      overflow: 'hidden',
      // For very small screens, allow a slightly smaller default header height
      '@media (max-width:600px)': {
        top: 'var(--app-header-height, 36px)',
      },
    }}
    >
      <Box sx={{ height: '100%', overflow: 'hidden' }}>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {reprocessing && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: (theme) => theme.zIndex.modal + 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.72)',
              backdropFilter: 'blur(2px)',
            }}
          >
            <Paper
              elevation={8}
              sx={{
                p: 3,
                minWidth: 280,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <CircularProgress size={44} />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Retraitement en cours
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {reprocessEngine === 'document_ai'
                    ? 'Document AI analyse le PDF, puis les données seront reparsées.'
                    : 'Les données stockées sont reparsées avec la dernière logique.'}
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}

        {/* Split View - PDF and Data */}
        {extractedData && !processing && (
          <Box sx={{ height: 'calc(100% - 40px)' }}>
            <ResizableSplitView
              leftPanel={(
                <ScrollablePdfViewer
                  pdfFile={pdfUrl}
                  highlightedPages={highlightedPages}
                  onPageClick={handlePdfPageClick}
                />
              )}
              rightPanel={(
                <Box sx={{
                  height: '100%',
                  overflow: 'auto',
                  p: 2,
                  backgroundColor: '#fafafa',
                }}
                >
                  {/* Confidence indicator */}
                  {(confidencePercent !== null || parserWarnings.length > 0) && (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      mb: 2,
                    }}
                    >
                      {confidencePercent !== null && (
                        <Chip
                          label={`Confiance IA : ${confidencePercent}%`}
                          color={confidenceChipColor}
                          variant="filled"
                          sx={{
                            alignSelf: 'flex-start',
                            fontWeight: 600,
                          }}
                        />
                      )}
                      {parserWarnings.length > 0 && (
                        <Alert
                          severity={lowConfidence ? 'warning' : 'info'}
                          sx={{ mb: 0 }}
                        >
                          {parserWarnings.map((warning) => (
                            <Typography
                              key={`parser-warning-${warning.replace(/[^a-zA-Z0-9]/g, '').slice(0, 20)}`}
                              variant="body2"
                            >
                              •
                              {' '}
                              {warning}
                            </Typography>
                          ))}
                        </Alert>
                      )}
                    </Box>
                  )}

                  {hasLocalEdits && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Modifications locales non enregistrées
                      </Typography>
                      <Typography variant="body2">
                        Les corrections faites ici servent à préparer l&apos;export CSV. Elles ne sont pas sauvegardées
                        dans Firestore : un rafraîchissement, un retour à la liste ou un retraitement rechargera le
                        résultat de l&apos;analyse et supprimera ces changements.
                      </Typography>
                    </Alert>
                  )}

                  {/* Metadata Form */}
                  <CertificateMetadataForm
                    initialData={metadata}
                    extractedData={extractedData}
                    onDataChange={handleMetadataChange}
                    collapsible
                  />

                  {/* Per-Page Tables */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: 1,
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Analyse par page
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Vérification et correction des tableaux extraits pour chaque page du PDF.
                      </Typography>
                    </Box>
                    <PerPageTableParser
                      pageTables={pageTables}
                      onDataChange={handlePageTablesChange}
                      onJumpToPage={handleJumpToPage}
                    />
                  </Box>

                  {/* Unified Data View */}
                  <Divider sx={{ my: 4 }} />
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: 1,
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Analyse du document complet
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Vue consolidée après assemblage de toutes les pages validées.
                      </Typography>
                    </Box>
                    <UnifiedDataView
                      pageTables={pageTables}
                      metadata={metadata}
                      onExport={handleExportCsv}
                    />
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{
                    display: 'flex',
                    gap: 2,
                    mt: 3,
                    justifyContent: 'flex-end',
                    p: 2,
                    backgroundColor: 'white',
                    borderRadius: 1,
                    boxShadow: 1,
                  }}
                  >
                    <Button variant="outlined" onClick={handleBackToList} startIcon={<ArrowBackIcon />}>
                      Retour à la liste
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleReprocessClick}
                      disabled={reprocessing}
                      sx={{
                        color: 'ourOrange.main',
                        borderColor: 'ourOrange.main',
                        '&:hover': {
                          borderColor: 'ourOrange.dark',
                          backgroundColor: 'ourOrange.light',
                        },
                      }}
                    >
                      {reprocessing ? 'Retraitement...' : 'Retraiter'}
                    </Button>
                    <Button variant="contained" onClick={handleExportBothCsv}>
                      Exporter CSV
                    </Button>
                  </Box>

                  {/* Validation Rules */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Validation automatique
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 0.75,
                        border: (theme) => (theme.palette.mode === 'light'
                          ? '1px solid rgba(0, 0, 0, 0.06)'
                          : '1px solid rgba(255, 255, 255, 0.1)'),
                        p: 1.5,
                        fontSize: '0.8rem',
                        lineHeight: 1.5,
                        overflow: 'auto',
                        mb: 0,
                      }}
                    >
                      {VALIDATION_RULES_DESCRIPTION}
                    </Box>
                  </Box>
                </Box>
              )}
              initialLeftWidth={50}
              minLeftWidth={30}
              maxLeftWidth={70}
            />
          </Box>
        )}
      </Box>

      {/* Reprocess Warning Dialog */}
      <Dialog
        open={showDiscardLocalEditsDialog}
        onClose={() => setShowDiscardLocalEditsDialog(false)}
        aria-labelledby="discard-local-edits-dialog-title"
      >
        <DialogTitle id="discard-local-edits-dialog-title">
          Quitter sans exporter ?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Les corrections locales ne sont pas sauvegardées dans Firestore. Si vous quittez cette page maintenant,
            elles seront perdues. Exportez les CSV si vous voulez conserver ces corrections.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDiscardLocalEditsDialog(false)}>
            Rester sur la page
          </Button>
          <Button color="warning" variant="contained" onClick={handleDiscardLocalEditsAndLeave}>
            Quitter sans exporter
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showReprocessDialog}
        onClose={handleReprocessCancel}
        aria-labelledby="reprocess-dialog-title"
        aria-describedby="reprocess-dialog-description"
      >
        <DialogTitle id="reprocess-dialog-title">
          Confirmer le retraitement
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="reprocess-dialog-description">
            Choisissez si vous voulez seulement relancer notre parser sur les données Document AI déjà stockées,
            ou relancer Document AI avant de parser à nouveau.
            {hasLocalEdits && ' Les modifications locales actuelles seront remplacées par le nouveau résultat.'}
          </DialogContentText>
          <Box sx={{
            mt: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
          >
            <FormControl>
              <FormLabel id="reprocess-engine-label">Traitement</FormLabel>
              <RadioGroup
                aria-labelledby="reprocess-engine-label"
                value={reprocessEngine}
                onChange={(event) => setReprocessEngine(event.target.value)}
              >
                <FormControlLabel
                  value="parser"
                  control={<Radio />}
                  label="Reparser uniquement (gratuit)"
                />
                <FormControlLabel
                  value="document_ai"
                  control={<Radio />}
                  label="Relancer Document AI puis reparser"
                />
              </RadioGroup>
            </FormControl>

            <FormControl>
              <FormLabel id="reprocess-scope-label">Portée</FormLabel>
              <RadioGroup
                aria-labelledby="reprocess-scope-label"
                value={reprocessScope}
                onChange={(event) => setReprocessScope(event.target.value)}
              >
                <FormControlLabel
                  value="document"
                  control={<Radio />}
                  label="Document complet"
                />
                <FormControlLabel
                  value="page"
                  control={<Radio />}
                  label={`Une seule page${selectedPdfPage ? ` (page sélectionnée : ${selectedPdfPage})` : ''}`}
                />
              </RadioGroup>
            </FormControl>

            {reprocessScope === 'page' && (
              <TextField
                label="Page à retraiter"
                type="number"
                value={reprocessPageNumber}
                onChange={(event) => setReprocessPageNumber(event.target.value)}
                inputProps={{ min: 1, step: 1 }}
                helperText="Cliquez une page du PDF pour la sélectionner automatiquement."
                size="small"
              />
            )}

            {reprocessEngine === 'document_ai' && (
              <Alert severity="warning">
                Cette option relance Document AI et peut consommer du quota. Le retraitement d&apos;une seule page
                conserve les autres pages déjà validées.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReprocessCancel} color="primary">
            Annuler
          </Button>
          <Button
            onClick={handleReprocessConfirm}
            color="warning"
            variant="contained"
            autoFocus
          >
            Confirmer le retraitement
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
